/**
 * /equipment/pressure-control/tests — recorded BOP pressure tests (api#1281,
 * BOP-2). Full width; a row opens the test on its own page. Recording one is a
 * page of its own too (/tests/new), because a test lists every component it
 * pressured.
 *
 * APPEND-ONLY on the API: a test is never edited. A correction is a new test.
 *
 * Every figure is the API's: the result (fail if any component failed), the
 * pressures in the tenant's unit, the day in the tenant's zone. The time of day
 * is the recorded instant rendered in the tenant's zone, never the device's.
 * GET /tests answers the latest 200 and is otherwise unpaginated, so the footer
 * says how many are shown.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Plus } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { PcBadge, PcSectionNav } from '../../components/pressure-control/PcSectionNav';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { usePcTests } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canRecordPcTest,
  PC_RESULT_FILL,
  PC_RESULT_LABEL,
  PC_RESULT_RAIL,
  PC_TESTS,
  pcTester,
  pcTestKindLabel,
} from '../../lib/pressure-control';
import type { PcTest } from '../../types/pressure-control';
import { DhEmpty, DhPanel } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';

/** The components on a test, by name, or `—`. */
export const pcTestComponents = (t: PcTest) => t.items.map((i) => i.name ?? '—').join(', ') || '—';

export default function PressureControlTestsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canRecord = canRecordPcTest(user?.role, elevated);
  const tz = useTenantTimeZone();
  const query = usePcTests();
  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];

  const open = (id: string) => openRecord(`${PC_TESTS}/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  const when = (t: PcTest) => formatInstant(t.testedAt, tz) ?? '—';

  return (
    <div data-testid="pc-tests-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure control"
        title="Pressure tests"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canRecord ? (
            <Link to={`${PC_TESTS}/new`} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Record test
            </Link>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PcSectionNav />
          <DhPanel
            eyebrow="Append-only"
            title="Tests"
            testId="pc-tests-register"
            footer={
              !query.isLoading && !failure && rows.length > 0
                ? `${rows.length} shown · newest first · the latest 200${canRecord ? '' : ' · only an admin, office staff or a supervisor can record a test'}`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading pressure tests" />
              </div>
            ) : failure ? (
              <LoadFailureNotice
                error={queryFailureError(query)}
                kind={failure}
                subject="pressure tests"
                allowed="admins, office staff, supervisors, dispatchers, field workers and logistics"
              />
            ) : rows.length === 0 ? (
              <DhEmpty>No pressure test has been recorded.{canRecord && ' Record one with Record test.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Tested</th>
                        <th scope="col" className="px-4 py-3">Kind</th>
                        <th scope="col" className="px-4 py-3">Components</th>
                        <th scope="col" className="px-4 py-3">Gauge</th>
                        <th scope="col" className="px-4 py-3">Stack</th>
                        <th scope="col" className="px-4 py-3">Tested by</th>
                        <th scope="col" className="px-4 py-3">Result</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t) => (
                        <tr
                          key={t.id}
                          data-testid="pc-test-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(t.id)}
                          onKeyDown={rowKeys(t.id)}
                          className={`cursor-pointer border-l-4 border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${PC_RESULT_RAIL[t.result] ?? 'border-l-transparent'}`}
                        >
                          <td className="px-4 py-3 text-sm text-white">{when(t)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcTestKindLabel(t.testKind)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcTestComponents(t)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{t.gauge.name ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{t.stackName ?? '—'}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{pcTester(t)}</td>
                          <td className="px-4 py-3"><PcBadge value={t.result} labels={PC_RESULT_LABEL} fills={PC_RESULT_FILL} testId="pc-result-badge" /></td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((t) => (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={rowKeys(t.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="flex flex-wrap items-center gap-2 text-sm font-extrabold text-white">
                        {when(t)}
                        <PcBadge value={t.result} labels={PC_RESULT_LABEL} fills={PC_RESULT_FILL} />
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">{pcTestKindLabel(t.testKind)} · gauge {t.gauge.name ?? '—'}</p>
                      <p className="mt-1 text-xs text-fieldiq-text">{pcTestComponents(t)}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
